import type { ExcelGridColumn, ExcelGridRow } from "../../shared/excel-grid/models/excel-grid.models";

export const ADMIN_CERTIFICATES_KPIS_ENVIRONMENTS = ["DEV", "QAS", "PRD"] as const;

export type AdminCertificatesKpisEnvironment = (typeof ADMIN_CERTIFICATES_KPIS_ENVIRONMENTS)[number];

export interface AdminCertificatesKpisEnvironmentOption {
  value: AdminCertificatesKpisEnvironment;
  label: string;
  database: string;
}

export const ADMIN_CERTIFICATES_KPIS_ENVIRONMENT_OPTIONS: AdminCertificatesKpisEnvironmentOption[] = [
  { value: "DEV", label: "DEV", database: "DEV" },
  { value: "QAS", label: "QAS", database: "QAS" },
  { value: "PRD", label: "PRD", database: "PRD" },
];

export type AdminCertificatesKpisPeriodCertificateStatusCode = "A1" | "A2" | "B1" | "B2" | "B3";
export type AdminCertificatesKpisPeriodBalticQuestionnaireStatusCode = "A" | "B1" | "B2";
export type AdminKpisTrendWindow = "30d" | "90d" | "180d" | "360d";

export type AdminKpisMissingCertificatesTableFilter =
  | {
      source: "periodCertificate";
      code: AdminCertificatesKpisPeriodCertificateStatusCode;
    }
  | {
      source: "periodBalticQuestionnaire";
      code: AdminCertificatesKpisPeriodBalticQuestionnaireStatusCode;
    };

export interface AdminCertificatesKpisDocumentRow {
  gridRowId: string;
  vesselName: string;
  openDate: string | null;
  imoNumber: string;
  vesselCode: string;
  vesselType: string;
  documentType: string;
  documentName: string;
  source: string;
  status: string;
  createdAt: string | null;
  bqStatus: string;
  bqSubmittedOn: string | null;
  expiresOn: string | null;
  expiryNotApplicable: boolean;
  nextSurveyDate: string | null;
  alert: string | null;
  inFleet: boolean;
  portOfIssueIsWhoApproved: boolean | null;
  deliveryDate: string | null;
  redeliveryDate: string | null;
  minRedeliveryDate: string | null;
  maxRedeliveryDate: string | null;
  contractType: string;
}

export interface AdminCertificatesKpisMissingCertificateRow {
  gridRowId: string;
  name: string;
  open: string | null;
  imo: string;
  code: string;
  type: string;
  contract: string;
  alerts: number;
  notices: number;
  missing: boolean;
  availableDocuments: string;
  availableCount: number;
  missingDocuments: string;
  missingCount: number;
  periodCertificateStatusCode: AdminCertificatesKpisPeriodCertificateStatusCode | null;
  periodBalticQuestionnaireStatusCode: AdminCertificatesKpisPeriodBalticQuestionnaireStatusCode | null;
}

export interface AdminCertificatesKpisChangedNameRow {
  gridRowId: string;
  code: string;
  currentName: string;
  oldNames: string;
}

export interface AdminCertificatesKpisKpi {
  label: string;
  count: number;
  percentage: number;
}

export interface AdminCertificatesKpisChartSegment {
  code: string;
  label: string;
  color: string;
  value: number;
}

export interface AdminCertificatesKpisPieSection {
  total: number;
  segments: AdminCertificatesKpisChartSegment[];
  kpis: AdminCertificatesKpisKpi[];
}

export interface AdminCertificatesKpisDailySnapshotMetric {
  count: number;
  percentage: number;
}

export interface AdminCertificatesKpisDailySnapshotTrendPoint {
  snapshotDate: string;
  periodFleetVesselsTotal: number;
  periodCertificateStatus: Record<
    AdminCertificatesKpisPeriodCertificateStatusCode,
    AdminCertificatesKpisDailySnapshotMetric
  >;
  periodBalticQuestionnaireStatus: Record<
    AdminCertificatesKpisPeriodBalticQuestionnaireStatusCode,
    AdminCertificatesKpisDailySnapshotMetric
  >;
}

export interface AdminCertificatesKpisFilterOptions {
  inFleet: boolean[];
  contractTypes: string[];
  vesselNames: string[];
  vesselTypes: string[];
  documentTypes: string[];
  documentNames: string[];
  alerts: string[];
}

export interface AdminCertificatesKpisResponse {
  updatedAt: string;
  actualEnvironment: AdminCertificatesKpisEnvironment;
  selectedEnvironment: AdminCertificatesKpisEnvironment;
  environmentOptions: AdminCertificatesKpisEnvironmentOption[];
  sourceVesselTypeOptions: string[];
  defaultExcludedVesselTypes: string[];
  selectedVesselTypes: string[];
  filterOptions: AdminCertificatesKpisFilterOptions;
  documents: AdminCertificatesKpisDocumentRow[];
  missingCertificates: AdminCertificatesKpisMissingCertificateRow[];
  periodFleetCertificateStatus: AdminCertificatesKpisPieSection;
  periodFleetBalticQuestionnaireStatus: AdminCertificatesKpisPieSection;
  dailySnapshotHistory: AdminCertificatesKpisDailySnapshotTrendPoint[];
  periodTcChangedNames: AdminCertificatesKpisChangedNameRow[];
}

export interface AdminKpisFilters {
  inFleet: boolean;
  contractTypes: string[];
  vesselNames: string[];
  vesselTypes: string[];
  documentTypes: string[];
  documentNames: string[];
  expirationDateBefore: string;
  surveyDateBefore: string;
  openDateBefore: string;
  alerts: string[];
}

export type AdminKpisLoadingState = "loading" | "ready" | "error";

export interface AdminKpisFilterPanelProps {
  filters: AdminKpisFilters;
  filterOptions: AdminCertificatesKpisFilterOptions;
  isRefreshing: boolean;
  onChange: (filters: AdminKpisFilters) => void;
  onRefresh: () => void;
  onReset: () => void;
}

export interface AdminKpisGridSectionProps {
  title: string;
  countLabel: string;
  rows: ExcelGridRow[];
  columns: ExcelGridColumn[];
  dataTest: string;
  emptyLabel: string;
  gridHeightClassName?: string;
  emptySectionMessage?: string;
  virtualized?: boolean;
  virtualizedMaxHeight?: number;
  useContentColumnWidths?: boolean;
}

export interface AdminKpisMultiSelectFilterProps {
  label: string;
  options: string[];
  value: string[];
  dataTest: string;
  disabled?: boolean;
  className?: string;
  onChange: (value: string[]) => void;
}

export interface AdminKpisPieSectionProps {
  title: string;
  section: AdminCertificatesKpisPieSection;
  dataTest: string;
  showSegmentPercentLabels?: boolean;
  activeSegmentCode?: string | null;
  onSegmentClick: (segmentCode: string) => void;
  onShowAll: () => void;
  onHideTable: () => void;
}

export interface AdminKpisPieLegendRowProps {
  segment: AdminCertificatesKpisChartSegment;
  isActive: boolean;
  onClick: (segmentCode: string) => void;
}

export interface AdminKpisPieChartProps {
  segments: AdminCertificatesKpisChartSegment[];
  total: number;
  showSegmentPercentLabels?: boolean;
}

export interface AdminKpisTrendWindowOption {
  value: AdminKpisTrendWindow;
  label: string;
}

export interface AdminKpisTrendPoint {
  snapshotDate: string;
  count: number;
  percentage: number;
  periodFleetVesselsTotal: number;
}

export interface AdminKpisTrendChartPoint extends AdminKpisTrendPoint {
  x: number;
  y: number;
}

export interface AdminKpisTrendChartData {
  chartWidth: number;
  points: AdminKpisTrendChartPoint[];
  tooltipPoints: AdminKpisTrendChartPoint[];
  dateAxisPoints: AdminKpisTrendChartPoint[];
  pathData: string;
  maxCount: number;
  midpointCount: number;
  firstDateLabel: string;
  lastDateLabel: string;
  latestPoint: AdminKpisTrendPoint | null;
}

export interface AdminKpisTrendLinePanelProps {
  activeFilter: AdminKpisMissingCertificatesTableFilter | null;
  history: AdminCertificatesKpisDailySnapshotTrendPoint[];
  periodFleetCertificateStatus: AdminCertificatesKpisPieSection;
  periodFleetBalticQuestionnaireStatus: AdminCertificatesKpisPieSection;
  selectedWindow: AdminKpisTrendWindow;
  onWindowChange: (window: AdminKpisTrendWindow) => void;
}

export interface AdminKpisPdfExportViewProps {
  environment: AdminCertificatesKpisEnvironment;
  periodFleetCertificateStatus: AdminCertificatesKpisPieSection;
  periodFleetBalticQuestionnaireStatus: AdminCertificatesKpisPieSection;
  activeTrendFilter: AdminKpisMissingCertificatesTableFilter | null;
  dailySnapshotHistory: AdminCertificatesKpisDailySnapshotTrendPoint[];
  selectedTrendWindow: AdminKpisTrendWindow;
}

export interface AdminKpisPdfSectionProps {
  title: string;
  section: AdminCertificatesKpisPieSection;
}

export interface AdminKpisPdfTrendLineProps {
  activeFilter: AdminKpisMissingCertificatesTableFilter | null;
  history: AdminCertificatesKpisDailySnapshotTrendPoint[];
  periodFleetCertificateStatus: AdminCertificatesKpisPieSection;
  periodFleetBalticQuestionnaireStatus: AdminCertificatesKpisPieSection;
  selectedWindow: AdminKpisTrendWindow;
}

export interface AdminKpisMetricTileProps {
  metric: AdminCertificatesKpisKpi;
}

export interface AdminKpisPdfMetricProps {
  metric: AdminCertificatesKpisKpi;
}

export interface AdminKpisPdfExportButtonProps {
  disabled: boolean;
  onClick: () => void;
}

export interface CertificatesKpisEnvironmentSelectorProps {
  value: AdminCertificatesKpisEnvironment;
  options: AdminCertificatesKpisEnvironmentOption[];
  loading: boolean;
  onChange: (value: AdminCertificatesKpisEnvironment) => void;
}

export interface CertificatesKpisVesselTypesSelectorProps {
  value: string[];
  options: string[];
  loading: boolean;
  onChange: (value: string[]) => void;
}

export interface AdminCertificatesKpisRequest {
  environment?: AdminCertificatesKpisEnvironment;
  vesselTypes?: string[];
}

export interface AdminKpisDateFilterProps {
  label: string;
  value: string;
  dataTest: string;
  onChange: (value: string) => void;
}

export interface AdminKpisPieSectionTableActionsProps {
  onShowAll: () => void;
  onHideTable: () => void;
}
